/**
 * Checks fixed-width export mappings for values that will be truncated.
 * Returns one warning per mapping that has at least one overflowing sample value.
 */
export function checkFixedWidthTruncation(mappings, sampleValues, widths) {
    const warnings = [];
    for (const m of mappings) {
        const width = widths[m.targetColumn];
        if (typeof width !== 'number' || width <= 0)
            continue;
        for (const record of sampleValues) {
            const v = record[m.sourceField] ?? '';
            if (v.length > width) {
                warnings.push({
                    severity: 'warn',
                    code: 'fixed-width.truncation',
                    message: `Value "${v}" (${v.length} chars) exceeds column width ${width} for "${m.targetColumn}" and will be truncated.`,
                    fieldName: m.sourceField,
                });
                break; // one warning per mapping
            }
        }
    }
    return warnings;
}
/** XML tag name rule: must start with letter or underscore, then any mix of letters/digits/hyphens/underscores. */
const XML_TAG_RE = /^[A-Za-z_][A-Za-z0-9_-]*$/;
/**
 * Walks an ExportStructureNode tree and returns warnings for node names
 * that are not valid XML element names.
 */
export function checkXmlTagNames(root) {
    if (!root)
        return [];
    const warnings = [];
    function walk(node) {
        if (!XML_TAG_RE.test(node.name)) {
            warnings.push({
                severity: 'warn',
                code: 'xml.invalid-tag-name',
                message: `Node name "${node.name}" is not a valid XML element name (must start with letter/underscore; only letters, digits, hyphens, underscores allowed).`,
            });
        }
        for (const child of node.children ?? [])
            walk(child);
    }
    walk(root);
    return warnings;
}
/**
 * Checks CSV mappings for sample values containing the delimiter when no quote
 * character is configured — those values will produce malformed CSV output.
 */
export function checkCsvDelimiterConflicts(mappings, sampleValues, config) {
    if (!config.delimiter || config.quote)
        return [];
    const warnings = [];
    for (const m of mappings) {
        for (const record of sampleValues) {
            const v = record[m.sourceField] ?? '';
            if (v.includes(config.delimiter)) {
                warnings.push({
                    severity: 'warn',
                    code: 'csv.delimiter-conflict',
                    message: `Sample value "${v}" in "${m.sourceField}" contains the delimiter "${config.delimiter}" and no quote character is configured — output will be malformed.`,
                    fieldName: m.sourceField,
                });
                break; // one warning per mapping
            }
        }
    }
    return warnings;
}
//# sourceMappingURL=format-warnings.js.map